import {
  LayoutDashboard,
  GitBranch,
  Bot,
  Users,
  TrendingUp,
  Building,
  Megaphone,
  Workflow,
  BarChart3,
  Plug,
  Settings,
  Search,
  Bell,
  ArrowUpRight,
  Flame,
  Mail,
  CheckCircle2,
  Activity,
} from "lucide-react";
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import IllustrativePill from "./IllustrativePill";

const sidebarItems = [
  { icon: LayoutDashboard, label: "Overview", active: true },
  { icon: GitBranch, label: "Pipeline" },
  { icon: Users, label: "Leads" },
  { icon: Bot, label: "CRM Intelligence" },
  { icon: TrendingUp, label: "Sales Intelligence" },
  { icon: Building, label: "Properties" },
  { icon: Megaphone, label: "Marketing" },
  { icon: Workflow, label: "Automation" },
  { icon: BarChart3, label: "Analytics" },
  { icon: Plug, label: "Integrations" },
  { icon: Settings, label: "Settings" },
];

const pipelineData = [
  { m: "Jan", v: 120 }, { m: "Feb", v: 138 }, { m: "Mar", v: 152 },
  { m: "Apr", v: 148 }, { m: "May", v: 178 }, { m: "Jun", v: 196 },
  { m: "Jul", v: 226 },
];

const activity = [
  { icon: Flame, color: "text-orange-400", label: "Lead scored as Hot", time: "2 min ago" },
  { icon: ArrowUpRight, color: "text-sapphire-glow", label: "Deal moved to Negotiation", time: "15 min ago" },
  { icon: Building, color: "text-emerald-400", label: "New property matched", time: "32 min ago" },
  { icon: Mail, color: "text-violet-400", label: "Follow-up email sent", time: "1 hr ago" },
];

const sources = [
  { name: "Website", value: 42, color: "#2563EB" },
  { name: "WhatsApp", value: 28, color: "#60A5FA" },
  { name: "Referral", value: 18, color: "#7C3AED" },
  { name: "Other", value: 12, color: "#22C55E" },
];

function Kpi({ label, value, sub }) {
  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3.5">
      <div className="text-[10.5px] tracking-wide text-platinum/55 uppercase">{label}</div>
      <div className="mt-1.5 font-display text-[22px] text-platinum leading-none">{value}</div>
      <div className="mt-1.5 text-[10.5px] text-platinum/45">{sub}</div>
    </div>
  );
}

export default function DashboardPreview() {
  return (
    <div className="rounded-2xl overflow-hidden border border-white/[0.08] bg-obsidian-800 ring-sapphire shadow-[0_30px_80px_-30px_rgba(37,99,235,0.45)] relative" data-testid="dashboard-preview">
      <IllustrativePill position="top-3 right-3" z="z-30" />
      {/* Top window bar */}
      <div className="flex items-center justify-between px-4 h-9 border-b border-white/[0.06] bg-[rgba(255,255,255,0.02)]">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
        </div>
        <div className="hidden sm:block text-[11px] tracking-[0.18em] text-platinum/50 uppercase truncate max-w-[45%]">esthera.realityos / dashboard</div>
        <div className="w-12" />
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className="hidden md:flex flex-col w-[180px] border-r border-white/[0.05] py-3">
          <div className="px-4 pb-3 flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-sapphire/20 border border-sapphire-glow/40 inline-flex items-center justify-center">
              <Activity className="w-3.5 h-3.5 text-sapphire-glow" />
            </div>
            <span className="text-[12px] font-medium text-platinum">Esthera RealityOS</span>
          </div>
          <nav className="px-2 mt-1 space-y-0.5">
            {sidebarItems.map((it) => (
              <div
                key={it.label}
                className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[11.5px] ${
                  it.active
                    ? "bg-sapphire/15 text-white border border-sapphire-glow/25"
                    : "text-platinum/60 hover:bg-white/[0.03] hover:text-platinum"
                }`}
              >
                <it.icon className="w-3.5 h-3.5" />
                <span>{it.label}</span>
              </div>
            ))}
          </nav>
        </aside>

        {/* Main */}
        <div className="flex-1 p-4 lg:p-5">
          {/* Header row */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] text-platinum/50 tracking-wide uppercase">Overview</div>
              <div className="font-display text-[18px] text-platinum">Good morning, Aarav</div>
            </div>
            <div className="flex items-center gap-2">
              <div className="hidden lg:flex items-center gap-2 h-8 px-2.5 rounded-md border border-white/[0.06] bg-white/[0.02] text-[11px] text-platinum/55">
                <Search className="w-3.5 h-3.5" />
                <span>Search…</span>
              </div>
              <div className="w-8 h-8 rounded-md border border-white/[0.06] bg-white/[0.02] inline-flex items-center justify-center">
                <Bell className="w-3.5 h-3.5 text-platinum/70" />
              </div>
            </div>
          </div>

          {/* KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi label="Pipeline Overview" value="1,238" sub="Active Pipeline" />
            <Kpi label="Leads Overview" value="842" sub="New Leads (This Month)" />
            <Kpi label="Tasks Overview" value="237" sub="Tasks Completed" />
            <Kpi label="AI Score" value="82%" sub="Average Lead Score" />
          </div>

          {/* Chart row */}
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr_0.9fr] gap-3">
            {/* Pipeline */}
            <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3.5">
              <div className="flex items-center justify-between">
                <div className="text-[11px] text-platinum/65 tracking-wide">Pipeline Overview</div>
                <div className="text-[10px] text-platinum/45">This Month ▾</div>
              </div>
              <div className="h-[120px] mt-2" style={{ minHeight: 120 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={pipelineData}>
                    <defs>
                      <linearGradient id="pipe-grad" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor="#2563EB" stopOpacity={0.55} />
                        <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
                    <XAxis dataKey="m" stroke="rgba(216,222,233,0.35)" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis hide />
                    <Tooltip contentStyle={{ background: "#0D1B3D", border: "1px solid rgba(96,165,250,0.35)", color: "#D8DEE9", fontSize: 11, borderRadius: 6 }} />
                    <Area type="monotone" dataKey="v" stroke="#60A5FA" strokeWidth={2} fill="url(#pipe-grad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* AI Activity Feed */}
            <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3.5">
              <div className="text-[11px] text-platinum/65 tracking-wide">AI Activity Feed</div>
              <ul className="mt-2 space-y-2">
                {activity.map((a, i) => (
                  <li key={i} className="flex items-center justify-between text-[11px]">
                    <span className="flex items-center gap-2 text-platinum/80">
                      <a.icon className={`w-3.5 h-3.5 ${a.color}`} />
                      {a.label}
                    </span>
                    <span className="text-platinum/40">{a.time}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Lead sources */}
            <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3.5">
              <div className="text-[11px] text-platinum/65 tracking-wide">Lead Sources</div>
              <div className="relative h-[120px] mt-1" style={{ minHeight: 120 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={sources} dataKey="value" innerRadius={32} outerRadius={48} paddingAngle={3} stroke="none">
                      {sources.map((s, i) => (
                        <Cell key={i} fill={s.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="font-display text-[16px] text-platinum leading-none">2,045</div>
                  <div className="text-[9px] text-platinum/45 mt-0.5">Total Leads</div>
                </div>
              </div>
              <ul className="mt-1.5 grid grid-cols-2 gap-y-1 gap-x-2 text-[10px]">
                {sources.map((s) => (
                  <li key={s.name} className="flex items-center gap-1.5 text-platinum/70">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />
                    {s.name} <span className="text-platinum/40 ml-auto">{s.value}%</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Automation Overview strip */}
          <div className="mt-3 flex items-center justify-between rounded-lg border border-white/[0.06] bg-white/[0.02] px-3.5 py-2.5">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-md bg-sapphire/15 border border-sapphire-glow/30 inline-flex items-center justify-center">
                <CheckCircle2 className="w-4 h-4 text-sapphire-glow" />
              </div>
              <div>
                <div className="text-[11.5px] text-platinum">Automation Overview</div>
                <div className="text-[10.5px] text-platinum/45">28 running workflows · active across pipeline</div>
              </div>
            </div>
            <button className="text-[11px] text-sapphire-glow hover:text-white">View Automations →</button>
          </div>
        </div>
      </div>
    </div>
  );
}
